// PinBox is a member's spot and how far, in CSS px, their pin and its bubble
// reach from it: left and right of the spot and up above it.
export interface PinBox { lat: number; lon: number; left: number; right: number; up: number }

// Padding is the room the map's controls cover on each side, in CSS px.
export interface Padding { top: number; right: number; bottom: number; left: number }

// fitPins frames a group's members so each whole pin and bubble shows in the
// part of a width by height map the padding leaves clear (spec 010
// 010 FR-005, 010 FR-008): the largest zoom, up to maxZoom, at which they all fit,
// centered in that part. It mirrors the apps' fitPins, with MapLibre's
// 512 px world at zoom 0 and the flat scale fine at one group's span.
export function fitPins(pins: PinBox[], width: number, height: number, pad: Padding, maxZoom: number, minZoom = 2): { lat: number; lon: number; zoom: number } | null {
  if (pins.length === 0 || width <= 0 || height <= 0) return null;
  const clearW = Math.max(width - pad.left - pad.right, 1);
  const clearH = Math.max(height - pad.top - pad.bottom, 1);
  const cosLat = Math.max(Math.cos((pins.reduce((a, p) => a + p.lat, 0) / pins.length) * Math.PI / 180), 0.01);
  const scale = (z: number) => (512 * 2 ** z) / 360;
  const extents = (z: number) => {
    const s = scale(z);
    let l = Infinity, r = -Infinity, t = Infinity, b = -Infinity;
    for (const p of pins) {
      const x = p.lon * s;
      const y = (-p.lat * s) / cosLat;
      l = Math.min(l, x - p.left);
      r = Math.max(r, x + p.right);
      t = Math.min(t, y - p.up);
      b = Math.max(b, y);
    }
    return { l, r, t, b };
  };
  const fits = (z: number) => {
    const e = extents(z);
    return e.r - e.l <= clearW && e.b - e.t <= clearH;
  };
  let zoom = maxZoom;
  if (!fits(maxZoom)) {
    let lo = minZoom, hi = maxZoom;
    for (let i = 0; i < 40; i++) {
      const mid = (lo + hi) / 2;
      if (fits(mid)) lo = mid; else hi = mid;
    }
    zoom = lo;
  }
  const e = extents(zoom);
  const s = scale(zoom);
  const cx = (e.l + e.r) / 2 - (pad.left - pad.right) / 2;
  const cy = (e.t + e.b) / 2 - (pad.top - pad.bottom) / 2;
  return { lat: (-cy * cosLat) / s, lon: cx / s, zoom };
}
